import _ from 'lodash';
import cn from 'classnames';
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, ModalBody } from 'reactstrap';
import * as Sentry from '@sentry/react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { Trans, useTranslation } from 'react-i18next';
import { Submit } from 'shared/components/Form';
import TitleWithButton from 'shared/components/TitleWithButton';
import Noop from 'shared/components/Noop';
import RedirectWithStatus from 'shared/components/RedirectWithStatus';
import CloseButton from 'shared/components/buttons/Close';
import Loadable from 'shared/components/Loadable';
import {
  APP_ID_USERPORTAL,
  SEO_TYPES,
  TRANSLATED_TYPES,
  LANGUAGE_LEVELS,
  ALL_JOBS_KEY,
  getTranslatedTypePath,
  getSliderStepByCurrency,
  getSliderMaxByCurrency,
  ALL_JOBS_LINK,
  OLD_SLUG_REDIRECTION,
  EXCLUSIVE_SEARCH_ITEM_KEYS,
  containsOldSlug,
} from 'shared/constants';
import useWindowSize from 'shared/hooks/useWindowSize';
import { createMoneyFormatter } from 'shared/lib/formatters';
import { buildSearchQuery } from 'shared/lib/searchHelpers';
import { buildSeoTitle, buildSeoDescription, developerSuffixSkills } from 'shared/lib/seoHelpers';
import { userportal as routes, getBaseUrlByAppId } from 'shared/routes';
import Page from 'userportal/components/Page';
import { NavigationColumn, ContentColumn } from 'userportal/components/Layout';
import Fulltext from 'userportal/components/Fulltext';
import FacetBox from 'userportal/components/AggregationBox/FacetBox';
import RangeBox from 'userportal/components/AggregationBox/RangeBox';
import RateRangeBox from 'userportal/components/AggregationBox/RateRangeBox';
import JobList from 'userportal/components/Job/List';
import NotFound from 'userportal/components/NotFound';
import { selectors as appSelectors } from 'userportal/ducks/app';
import { selectors as authSelectors } from 'userportal/ducks/auth';
import {
  actions as jobSearchActions,
  selectors as jobSearchSelectors,
} from 'userportal/ducks/jobSearch';
import useJobSearchQuery from 'userportal/hooks/useJobSearchQuery';
import apiClient from 'userportal/lib/ApiClient';
import googleAnalytics from 'userportal/lib/googleAnalytics';
import Intro from './Intro';
import CompanyPage from '../Company/Page';
import styles from './Home.module.scss';

const searchInitialState = {
  loading: null,
  error: null,
  jobs: {
    took: null,
    total: 0,
    hits: [],
    aggregations: {},
  }
};

const MobileWrapper = ({ t, filterOpened, onFilterSubmit, onFilterClose, children }) => (
  <Modal isOpen={filterOpened} className="filter-modal" style={{ width: '100%', maxWidth: '100%' }}>
    <ModalBody>
      <TitleWithButton variant="h2" title={t('page.home.filterTitle')}>
        <CloseButton onClick={onFilterClose}>
          {t('common:linkButton.close')}
        </CloseButton>
      </TitleWithButton>
      {children}
      <div className={styles.filterSubmit}>
        <Submit
          fullWidth
          iconClassName="la la-sliders-h"
          title={t('navigation.openFilter')}
          onClick={onFilterSubmit}
        />
      </div>
    </ModalBody>
  </Modal>
);

/*
search flow (refactoring needed!)
- onmount se nacitaji search items z /dials (dulezite hlavne pro country, city a skills ktere nejsou v prekladech)
- pri jakekoli zmene se prida item do reduxu (drzi mimo jine item titles) pro cache
- provede se redirect
- pri zmene location.search se do reduxu vlozi items vyparsovane z location.search a to vyvola search
*/
const HomePage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('userportal');
  const loggedIn = useSelector(authSelectors.isLoggedIn);
  const currency = useSelector(authSelectors.getUserCurrency);
  const sessionId = useSelector(appSelectors.getSessionId);
  const {
    items: searchItems,
    loading: searchItemsLoading,
    id: searchId,
  } = useSelector(jobSearchSelectors.getState);
  const [searchState, setSearchState] = useState(searchInitialState);
  const [searchAggregations, setSearchAggregations] = useState(null);
  const [loadingAggregations, setLoadingAggregations] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [filterOpened, setFilterOpened] = useState(false);
  const [deferredState, setDeferredState] = useState({ changed: false, searchItems });

  const { isMobileView } = useWindowSize();
  const jobSearchQuery = useJobSearchQuery(location);

  const toggleFilterOpened = useCallback(() => setFilterOpened(prev => !prev), [setFilterOpened]);

  const qs = queryString.parse(location.search);
  const isHomepage = jobSearchQuery.items.length === 0 && searchItems.length === 0 && !qs.page && !qs.sort && !(ALL_JOBS_KEY in qs);
  const formatMoney = createMoneyFormatter(i18n.language);

  useEffect(() => {
    if (isHomepage) {
      googleAnalytics.event('homepage_view');
    }
  }, []);

  const searchJobs = useCallback(async (externalItems) => {
    if (searchState.loading) return;
    const query = externalItems || jobSearchQuery.items;
    setSearchState(prev => ({ ...prev, error: null, loading: true }));

    const payload = {
      q: buildSearchQuery(query),
      params: { ...jobSearchQuery.paramsMap, id: query.length > 0 ? searchId : null },
      sessionId,
    };

    try {
      const jobs = await apiClient.post('/jobs/search', payload);

      dispatch(jobSearchActions.setSearchId(jobs.id));
      setSearchState(prev => ({ ...prev, error: null, initialSearch: false, loading: false, jobs }));
    } catch (err) {
      setSearchState(prev => ({ ...prev, error: err.error, initialSearch: false, loading: false }));
      Sentry.withScope((scope) => {
        scope.setExtra('data', JSON.stringify(payload));
        Sentry.captureException(err);
      });
    }
    setSearchAggregations(null);
  }, [searchState.loading, location.pathname, location.search, setSearchState, setSearchAggregations, dispatch]);

  const reloadAggs = async (q) => {
    setLoadingAggregations(true);

    const payload = {
      q: buildSearchQuery(q),
      aggsOnly: true,
    };

    try {
      const result = await apiClient.post('/jobs/search', payload);

      setSearchAggregations(result.aggregations);
    } catch (err) {
      Sentry.withScope((scope) => {
        scope.setExtra('data', JSON.stringify(payload));
        Sentry.captureException(err);
      });
    }
    setLoadingAggregations(false);
  };

  useEffect(() => {
    if (deferredState.changed) {
      reloadAggs(deferredState.searchItems);
    }
  }, [deferredState]);

  useEffect(() => {
    if (jobSearchQuery.items.length > 0) {
      dispatch(jobSearchActions.loadItems(jobSearchQuery.items, jobSearchQuery.paramsMap));
    } else {
      dispatch(jobSearchActions.syncWithLocation(jobSearchQuery.items, jobSearchQuery.paramsMap));
    }
  }, []);

  useEffect(() => {
    if (containsOldSlug(location.pathname)) {
      const newPathname = OLD_SLUG_REDIRECTION.reduce((acc, [oldSlug, newSlug]) => {
        acc = acc.replace(oldSlug, newSlug);
        return acc;
      }, location.pathname);

      window.location = `${newPathname}${location.search}`;
      return;
    }

    dispatch(jobSearchActions.syncWithLocation(jobSearchQuery.items, jobSearchQuery.paramsMap));
  }, [location.pathname, location.search]);

  useEffect(() => {
    setDeferredState({ changed: false, searchItems });
    if (!isHomepage) {
      searchJobs();
    } else {
      setSearchState(searchInitialState);
    }
  }, [JSON.stringify(searchItems), i18n.language, qs.page, qs.sort, isHomepage]);

  const updateLocation = useCallback((items = []) => {
    const route = jobSearchQuery.getUrl(items, jobSearchQuery.params);
    if (route !== routes.root) {
      navigate(route);
    } else {
      navigate(ALL_JOBS_LINK);
    }

    // neni treba, zpusobuje duplicitni Redux action se starym payloaded
    // dispatch(jobSearchActions.syncWithLocation(jobSearchQuery.items, jobSearchQuery.paramsMap));
  }, [location.pathname, location.search, history, dispatch]);

  const onRemoveSuggestion = useCallback((item) => {
    const exists = searchItems.find(s => s.id === item.id);
    if (exists) {
      updateLocation(searchItems.filter(s => s.id !== item.id));
      googleAnalytics.event('tag_remove', item);
    }
  }, [searchItems, updateLocation]);

  const isDeferredChanged = useCallback((deferredItems) => {
    if (searchItems.length !== deferredItems.length) {
      return true;
    }

    const searchItemsRest = searchItems.filter(item => !deferredItems.find(s => item.slug && s.slug ? (item.type === s.type && item.slug === s.slug) : (item.type === s.type && item.title === s.title)));
    if (searchItemsRest.length > 0) {
      return true;
    }

    const deferredItemsRest = deferredItems.filter(item => !searchItems.find(s => item.slug && s.slug ? (item.type === s.type && item.slug === s.slug) : (item.type === s.type && item.title === s.title)));
    if (deferredItemsRest.length > 0) {
      return true;
    }

    return false;
  }, [searchItems]);

  const handleDeferredItem = useCallback((item) => {
    const exists = deferredState.searchItems.find(s => item.slug && s.slug ? (item.type === s.type && item.slug === s.slug) : (item.type === s.type && item.title === s.title));

    if (exists) {
      setDeferredState((prev) => {
        const items = prev.searchItems.filter(s => !(item.slug && s.slug ? (item.type === s.type && item.slug === s.slug) : (item.type === s.type && item.title === s.title)));
        return {
          changed: isDeferredChanged(items),
          searchItems: items
        };
      });
    } else {
      setDeferredState((prev) => {
        const items = EXCLUSIVE_SEARCH_ITEM_KEYS.includes(item.type)
          ? prev.searchItems.filter(s => item.type !== s.type)
          : prev.searchItems;
        const nextItems = [...items, item];
        return {
          changed: isDeferredChanged(nextItems),
          searchItems: nextItems
        };
      });
    }
    googleAnalytics.event(exists ? 'facet_remove' : 'facet_add', item);
  }, [deferredState.searchItems, setDeferredState, isDeferredChanged]);

  const scrollBackToItem = useCallback((item) => {
    if (!isMobileView) {
      return;
    }

    setTimeout(() => {
      let elmId;
      try {
        elmId = item.type === 'homeoffice' || item.type === 'rate' ? `${item.type}-box` : `${item.type}-${item.slug}`;
        document.getElementById(elmId).scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (err) {
        Sentry.withScope((scope) => {
          scope.setExtra('data', { item, elmId });
          Sentry.captureException(err);
        });
      }
    }, 300);
  }, [isMobileView]);

  const onSuggestionSelect = useCallback((inItem, defer = false) => {
    const item = {
      ...inItem,
      required: false
    };

    if (defer) {
      handleDeferredItem(item);
      // scrollBackToItem(item);
      return;
    }

    let items = searchItems;
    const exists = items.find(s => item.slug && s.slug ? (item.type === s.type && item.slug === s.slug) : (item.type === s.type && item.title === s.title));

    if (exists) {
      onRemoveSuggestion(exists);
    } else {
      dispatch(jobSearchActions.addItem(item));
      if (EXCLUSIVE_SEARCH_ITEM_KEYS.includes(item.type)) {
        items = items.filter(si => si.type !== item.type);
      }
      updateLocation([...items, item]);
    }
    // scrollBackToItem(item);
    googleAnalytics.event(exists ? 'tag_remove' : 'tag_add', item);
  }, [searchItems, onRemoveSuggestion, dispatch, updateLocation, handleDeferredItem, scrollBackToItem]);

  const handleFieldClick = useCallback((item, defer = false) => () => {
    onSuggestionSelect({ type: 'field', title: item.title, slug: item.slug }, defer);
  }, [onSuggestionSelect]);

  const handleRoleClick = useCallback((item, defer = false) => () => {
    onSuggestionSelect({ type: 'role', title: item.title, slug: item.slug, required: !!item.required }, defer);
  }, [onSuggestionSelect]);

  const handleSkillClick = useCallback(item => () => {
    onSuggestionSelect({
      type: 'skill',
      title: item.title,
      slug: item.slug,
      // no level by default
      // levelId: item.levelId,
      required: !!item.required
    });
  }, [onSuggestionSelect]);

  const handleEmploymentTypeClick = useCallback((item, defer = false) => () => {
    onSuggestionSelect({ type: 'employment', title: item.title, slug: item.slug }, defer);
  }, [onSuggestionSelect]);

  const handleCountryClick = useCallback((item, defer = false) => () => {
    onSuggestionSelect({ type: 'country', title: item.title || item.country, slug: item.slug || item.countrySlug }, defer);
  }, [onSuggestionSelect]);

  const handleCityClick = useCallback((item, defer = false) => () => {
    onSuggestionSelect({ type: 'city', title: item.title || item.city, slug: item.slug || item.citySlug }, defer);
  }, [onSuggestionSelect]);

  const handleRemoteWorkClick = useCallback((item, defer = false) => () => {
    onSuggestionSelect({ type: 'remoteWork', title: item.title, slug: item.slug }, defer);
  }, [onSuggestionSelect]);

  const handleCompanySizeClick = useCallback((item, defer = false) => () => {
    onSuggestionSelect({ type: 'companySize', title: item.title, slug: item.slug }, defer);
  }, [onSuggestionSelect]);

  const handleSuitableForClick = useCallback((item, defer = false) => () => {
    onSuggestionSelect({ type: 'suitableFor', title: item.title, slug: item.slug }, defer);
  }, [onSuggestionSelect]);

  const handleCompanyTypeClick = useCallback((item, defer = false) => () => {
    onSuggestionSelect({ type: 'companyType', title: item.title, slug: item.slug }, defer);
  }, [onSuggestionSelect]);

  const handleBenefitClick = useCallback(item => () => {
    onSuggestionSelect({ type: 'benefit', title: item.title, slug: item.slug, required: true });
  }, [onSuggestionSelect]);

  const handleSelectRate = useCallback((rate, not = false, defer = false) => {
    onSuggestionSelect({ type: 'rate', title: rate.toString(), slug: rate.toString(), not }, defer);
  }, [onSuggestionSelect]);

  const handleSelectHomeoffice = useCallback((value, not = false, defer = false) => {
    onSuggestionSelect({ type: 'homeoffice', title: value.toString(), slug: value.toString(), not }, defer);
  }, [onSuggestionSelect]);

  const handleRateClick = useCallback(rate => () => {
    handleSelectRate(rate);
  }, [handleSelectRate]);

  const onChangeLevel = useCallback((skill) => {
    const { type, slug } = skill;
    const max = type === 'language' ? 6 : 3;
    updateLocation(searchItems.map((item) => {
      if (item.type === type && item.slug === slug) {
        const nextLevel = ~~item.levelId < max ? ~~item.levelId + 1 : 0;
        googleAnalytics.event('tag_change_level', { ...item, nextLevel });
        return {
          ...item,
          levelId: ~~item.levelId < max ? ~~item.levelId + 1 : 0
        };
      }

      return item;
    }));
  }, [searchItems, updateLocation]);

  const onToggleItemRequired = useCallback((skill) => {
    const { type, slug } = skill;
    updateLocation(searchItems.map((item) => {
      if (item.type === type && item.slug === slug) {
        googleAnalytics.event('tag_toggle_required', { ...item, required: !item.required });
        return {
          ...item,
          required: !item.required,
        };
      }

      return item;
    }));
  }, [searchItems, updateLocation]);

  const getNextMode = useCallback((mode) => {
    if (!mode) return 'u';
    if (mode === 'u') return 'd';
    return undefined;
  }, []);

  const onToggleItemMode = useCallback((skill) => {
    const { type, slug } = skill;
    updateLocation(searchItems.map((item) => {
      if (item.type === type && item.slug === slug) {
        const nextMode = getNextMode(item.mode);
        googleAnalytics.event('tag_toggle_mode', { ...item, mode: nextMode });
        return {
          ...item,
          mode: nextMode
        };
      }

      return item;
    }));
  }, [searchItems, updateLocation]);

  const onRemoveSuggestionByType = useCallback(type => () => {
    const exists = searchItems.find(s => s.type === type);
    if (exists) {
      // updateLocation(searchItems.filter(s => s.type !== type));
      setDeferredState(prev => ({
        changed: true,
        searchItems: prev.searchItems.filter(s => type !== s.type)
      }));
    }
  }, [searchItems, setDeferredState]);

  const onRemoveLastRequiredSuggestion = useCallback(() => {
    if (searchItems.length > 0) {
      const removedIndex = _.findLastIndex(searchItems, item => !item.not);
      const removed = searchItems[removedIndex];
      if (removed) {
        googleAnalytics.event('tag_remove_last', removed);
      }
      updateLocation(searchItems.filter((_i, i) => i !== removedIndex));
    }
  }, [searchItems, updateLocation]);

  const onRemoveLastIgnoredSuggestion = useCallback(() => {
    if (searchItems.length > 0) {
      const removedIndex = _.findLastIndex(searchItems, item => item.not);
      const removed = searchItems[removedIndex];
      if (removed) {
        googleAnalytics.event('tag_remove_last', removed);
      }
      updateLocation(searchItems.filter((_i, i) => i !== removedIndex));
    }
  }, [searchItems, updateLocation]);

  const onDragDrop = useCallback((result) => {
    // console.log(searchItems, result);
    const { draggableId, source, destination } = result;
    if (!(source && destination)) {
      return;
    }

    if (destination.droppableId === source.droppableId) {
      // move order only
      // TODO: change order in particular section
    } else {
      // move between
      const draggedItem = searchItems.find(({ id }) => id === draggableId);
      const items = searchItems.map(item => ({
        ...item,
        not: item.id === draggableId ? destination.droppableId === 'ignored' : item.not
      }));
      updateLocation(items);
      googleAnalytics.event('drag_search_item', draggedItem);
    }
  }, [searchItems, updateLocation]);

  const onRemoveIgnored = useCallback(() => {
    const hasIgnored = searchItems.some(({ not }) => not);
    if (hasIgnored) {
      googleAnalytics.event('clear_ignored');
      updateLocation(searchItems.filter(({ not }) => !not));
    }
  }, [searchItems, updateLocation]);

  const onSuggestionsContainerDisplay = useCallback(opened => setSuggesting(opened), [setSuggesting]);

  const onSearchSubmit = useCallback((event, additionalItem) => {
    event.preventDefault();
    const items = [...searchItems, additionalItem].filter(Boolean);
    if (suggesting || items.length > 0) {
      searchJobs(items);
    } else {
      navigate(ALL_JOBS_LINK);
    }
  }, [searchItems, searchJobs]);

  // const getSearchItemsByType = useCallback(type => searchItems.filter(i => i.type === type), [searchItems]);
  const getSearchItemsByType = useCallback(type => deferredState.searchItems.filter(i => i.type === type), [deferredState.searchItems]);

  const getSearchItemTitle = useCallback((title, { type, levelId, required, mode, not }) => {
    const parts = [];
    // Java (zacatecnik nebo vyssi, je vyzadovano)
    if (levelId > 0) {
      let level = type === 'language' ? LANGUAGE_LEVELS[levelId] : t(`common:tag.level-${levelId}`);
      if (mode === 'u') level += ` ${t('common:tag.modeUp')}`;
      else if (mode === 'd') level += ` ${t('common:tag.modeDown')}`;

      parts.push(level);
    }

    if (required && !not) {
      parts.push(t('common:form.isRequired'));
    }

    if (not) {
      parts.push(t('common:excluded'));
    }

    if (parts.length > 0) {
      return `${title} (${parts.join(', ')})`;
    }

    return title;
  }, [t]);

  const formatHomefficeValueLabel = useCallback((value, not = false) => {
    if (~~value === 0) {
      return t('common:suggestion.typeHomeofficeNa');
    }

    return `${t(not ? 'common:suggestion.typeHomeofficeInverse' : 'common:suggestion.typeHomeoffice')} ${t(
      'common:suggestion.typeHomeofficeValue',
      { postProcess: 'interval', count: ~~value }
    )}`;
  }, [t]);

  const handleDeferredFacet = fn => item => fn(item, isMobileView);

  const handleResetDeferred = useCallback(() => {
    setDeferredState({ changed: false, searchItems });
    setFilterOpened(false);
    setSearchAggregations(null);
  }, [setDeferredState, setSearchAggregations, searchItems]);

  useEffect(() => {
    if (!isMobileView) {
      handleResetDeferred();
    }
  }, [isMobileView, handleResetDeferred]);

  const handleSubmitDeferred = useCallback(() => {
    dispatch(jobSearchActions.addItems(deferredState.searchItems));
    setFilterOpened(false);
    updateLocation(deferredState.searchItems);
  }, [deferredState.searchItems, dispatch, updateLocation]);

  const searchTitleSeoPart = useMemo(() => {
    const seoSearchItems = searchItems.filter(({ type, title }) => SEO_TYPES.includes(type) && (title || type === 'suitableFor'));
    const companies = seoSearchItems.filter(({ type }) => type === 'company');
    const skillsCount = seoSearchItems.filter(({ type }) => type === 'skill').length;
    const rolesCount = seoSearchItems.filter(({ type }) => type === 'role').length;
    let otherSearchItems = [];
    SEO_TYPES.filter(type => type !== 'company').forEach((type) => {
      otherSearchItems = otherSearchItems.concat(seoSearchItems.filter(item => item.type === type));
    });

    const companiesTitle = companies.map(item => getSearchItemTitle(item.title, item)).join(', ').trim();
    const otherItemsTitle = otherSearchItems.map((item) => {
      const { title, type, slug } = item;
      if (TRANSLATED_TYPES.includes(type) && slug) {
        return getSearchItemTitle(t(getTranslatedTypePath(type, slug)), item);
      }

      if (
        rolesCount === 0
        && skillsCount === 1
        && type === 'skill'
        && !item.levelId
        && !item.required
        && !item.not
        && developerSuffixSkills.includes(slug)
      ) {
        return getSearchItemTitle(`${title} developer`, item);
      }

      return getSearchItemTitle(title, item);
    }).join(', ').trim();
    return companiesTitle.length > 0
      ? `${otherItemsTitle}${otherItemsTitle.length > 0 ? ', ' : ''}${companiesTitle}`
      : otherItemsTitle;
  }, [searchItems, getSearchItemTitle, isHomepage]);

  const searchTitleFull = useMemo(() => {
    const others = searchItems.filter(({ type }) => !SEO_TYPES.includes(type)).map((item) => {
      const { title, type, slug, not } = item;
      if (TRANSLATED_TYPES.includes(type) && slug) {
        return getSearchItemTitle(t(getTranslatedTypePath(type, slug)), item);
      }

      if (type === 'language') {
        return getSearchItemTitle(t(`common:language.${slug}`), item);
      }

      if (type === 'rate') {
        return getSearchItemTitle(t(not ? 'common:suggestion.typeRateInverse' : 'common:suggestion.typeRate', { value: formatMoney(slug, currency) }), item);
      }

      if (type === 'homeoffice') {
        if (~~slug === 0) {
          return t('common:suggestion.typeHomeofficeNa');
        }

        return `${t(not ? 'common:suggestion.typeHomeofficeInverse' : 'common:suggestion.typeHomeoffice')} ${t(
          'common:suggestion.typeHomeofficeValue',
          { postProcess: 'interval', count: ~~slug }
        )}`;
      }

      if (type === 'text') {
        return t(not ? 'common:suggestion.typeTextInverse' : 'common:suggestion.typeText', { value: slug });
      }

      return getSearchItemTitle(title, item);
    }).join(', ').trim();

    return [searchTitleSeoPart, others].filter(Boolean).join(', ');
  }, [searchTitleSeoPart, searchItems, getSearchItemTitle, isHomepage]);

  const topSkills = useMemo(() => {
    if (searchItems.length === 0) return '';

    const map = searchState.jobs.hits.reduce((acc, curr) =>
      curr.skills.reduce((acc2, curr2) => {
        acc2[curr2.title] = (acc2[curr2.title] || 0) + 1;
        return acc2;
      }, acc), {});

    const skills = Object.entries(map)
      .map(([key, value]) => ({ title: key, count: value }))
      .sort((a, b) => a.count > b.count ? -1 : 1)
      .slice(0, 10);

    if (skills.length < 3) return '';
    return `${skills.map(({ title }) => title).join(', ')}, ...`;
  }, [searchItems, searchState.jobs.hits]);

  const AGGREGATIONS = [
    { type: 'field', key: 'fields', titleKey: 'field', fn: handleFieldClick, multiselect: true },
    { type: 'role', key: 'roles', titleKey: 'role', fn: handleRoleClick, multiselect: true },
    { type: 'employment', key: 'employmentTypes', titleKey: 'employment', fn: handleEmploymentTypeClick, multiselect: true },
    { type: 'rate' },
    { type: 'homeoffice' },
    { type: 'remoteWork', key: 'remoteWork', titleKey: 'remoteWork', fn: handleRemoteWorkClick, multiselect: true },
    { type: 'companySize', key: 'companySizes', titleKey: 'companySize', fn: handleCompanySizeClick, multiselect: true },
    { type: 'companyType', key: 'companyTypes', titleKey: 'companyType', fn: handleCompanyTypeClick, multiselect: false },
    { type: 'country', key: 'countries', titleKey: 'country', fn: handleCountryClick, multiselect: true },
    { type: 'city', key: 'cities', titleKey: 'city', fn: handleCityClick, multiselect: true },
    { type: 'suitableFor', key: 'suitableFor', titleKey: 'suitableFor', fn: handleSuitableForClick, multiselect: true },
  ];


  const mergeAggregations = useCallback((resultAggregations) => {
    if (searchItems.length === 0) {
      return resultAggregations;
    }

    const aggs = AGGREGATIONS.filter(agg => !!agg.key).reduce((acc, agg) => {
      if (!(agg.key in resultAggregations)) {
        acc[agg.key] = {
          buckets: searchItems
            .filter(item => item.type === agg.type)
            .map(item => ({
              slug: item.slug,
              title: item.title,
              count: 0
            })),
          count: 0,
          key: agg.key,
          type: 'slug'
        };
      } else {
        const buckets = resultAggregations[agg.key].buckets || [];
        const items = searchItems
          .filter(item => item.type === agg.type)
          .filter(item => !buckets.find(bucket => bucket.slug === item.slug))
          .map(item => ({
            slug: item.slug,
            title: item.title,
            count: 0
          }));
        acc[agg.key] = {
          ...resultAggregations[agg.key],
          buckets: buckets.concat(items)
        };
      }
      return acc;
    }, {});

    aggs.rate = resultAggregations.rate;
    const rate = searchItems.find(item => item.type === 'rate');
    if (rate && !(resultAggregations.rate && resultAggregations.rate.max)) {
      aggs.rate = {
        key: 'rate',
        type: 'range',
        min: 0,
        max: Math.max(~~rate.slug, 0) + 1
      };
    }

    aggs.homeoffice = resultAggregations.homeoffice;
    const homeoffice = searchItems.find(item => item.type === 'homeoffice');
    if (homeoffice && !(resultAggregations.homeoffice && resultAggregations.homeoffice.max)) {
      aggs.homeoffice = {
        key: 'homeoffice',
        type: 'range',
        min: 0,
        max: Math.min(~~homeoffice.slug || 5, 5)
      };
    }

    return aggs;
  }, [searchItems, AGGREGATIONS]);

  const { jobs: { hits, aggregations: resultAggregations, ...jobsStats }, loading, error } = searchState;
  const hasFacets = searchItems.some(item => item.type !== 'skill');
  const aggregations = mergeAggregations(searchAggregations || resultAggregations);
  // const aggregations = resultAggregations; // debug

  const helmets = useMemo(() => {
    const max = aggregations && aggregations.rate ? aggregations.rate.max : 0;
    const maxRate = max ? formatMoney(max, currency) : null;
    return {
      title: searchTitleSeoPart.length > 0
        ? buildSeoTitle(t, `${searchTitleSeoPart} - ${t('userportal:jobs.suffix')}`)
        : buildSeoTitle(t),
      description: searchTitleSeoPart.length > 0
        ? buildSeoDescription(t, searchTitleSeoPart, topSkills, maxRate)
        : buildSeoDescription(t, '', topSkills, maxRate)
    };
  }, [searchTitleSeoPart, topSkills, aggregations, formatMoney, currency]);

  const companyOnlySearchSlug = useMemo(() => jobsStats.total === 0 && jobsStats.took !== null && searchItems.length === 1 && searchItems.every(item => item.type === 'company') ? searchItems[0].slug : undefined, [searchItems, jobsStats]);

  if (jobSearchQuery.isOldUrl) {
    return (<RedirectWithStatus to={jobSearchQuery.getNewUrl()} status={301} />);
  }

  const seoUrl = `${getBaseUrlByAppId(APP_ID_USERPORTAL)}${jobSearchQuery.getPathname(searchItems)}`;

  const isLoading = loading || loading === null || searchItemsLoading;
  const nothingFound = jobsStats.total === 0 && !hasFacets;
  const FilterWrapper = isMobileView ? MobileWrapper : Noop;

  return (
    <>
      <Page
        withBackground
        noNavigationBottomPadding
        initialContainer={false}
        header={(
          <Fulltext
            currency={currency}
            disabled={loading}
            items={searchItems}
            loading={searchItemsLoading}
            onSuggestionSelect={onSuggestionSelect}
            onChangeLevel={onChangeLevel}
            onToggleItemRequired={onToggleItemRequired}
            onToggleItemMode={onToggleItemMode}
            onRemoveSuggestion={onRemoveSuggestion}
            onRemoveLastRequiredSuggestion={onRemoveLastRequiredSuggestion}
            onRemoveLastIgnoredSuggestion={onRemoveLastIgnoredSuggestion}
            onSearchSubmit={onSearchSubmit}
            onSuggestionsContainerDisplay={onSuggestionsContainerDisplay}
            onDragDrop={onDragDrop}
            onRemoveIgnored={onRemoveIgnored}
          />
        )}
        helmetTitle={helmets.title}
        helmetDescription={helmets.description}
        seoUrl={seoUrl}
      >
        {isHomepage ? (
          <Intro
            withSeoLinks
            loggedIn={loggedIn}
            currency={currency}
            searchItems={searchItems}
            onRoleClick={handleRoleClick}
            onSkillClick={handleSkillClick}
            onEmploymentTypeClick={handleEmploymentTypeClick}
            onCompanySizeClick={handleCompanySizeClick}
            onCountryClick={handleCountryClick}
            onCityClick={handleCityClick}
            onBenefitClick={handleBenefitClick}
            onFieldClick={handleFieldClick}
            onRateClick={handleRateClick}
            onSuitableForClick={handleSuitableForClick}
          />
        ) : companyOnlySearchSlug ? (
          <CompanyPage isHomepageView slug={companyOnlySearchSlug} />
        ) : (
          <div className={cn('container pt32', { relative: isLoading })}>
            <Loadable
              loading={isLoading}
              canRender={hits.length > 0}
              error={error}
              splashOnTop
              spinnerClassName={styles.spinner}
              render={() => nothingFound && !isLoading ? (
                <NotFound
                  title={(
                    <>
                      {searchTitleFull} - {t('jobs.suffix')}
                    </>
                  )}
                  lines={[
                    t('jobs.nothingFound.p1', { searchTitle: searchTitleFull }),
                    loggedIn ? (
                      <Trans i18nKey="userportal:jobs.nothingFound.p2user">
                        <Link to={routes.user.profile.view} />
                      </Trans>
                    ) : (
                      <Trans i18nKey="userportal:jobs.nothingFound.p2">
                        <Link to={routes.signup} />
                      </Trans>
                    )
                  ]}
                  ctaTitle={t('jobs.nothingFound.cta')}
                  ctaRoute={ALL_JOBS_LINK}
                />
              ) : (
                <div className="row">
                  <ContentColumn id="job-list">
                    <JobList
                      withSeoLinks
                      isLoading={isLoading}
                      total={jobsStats.total}
                      pageSize={jobsStats.pageSize}
                      jobs={hits}
                      loggedIn={loggedIn}
                      currency={currency}
                      searchTitle={searchTitleFull}
                      searchItems={searchItems}
                      onRoleClick={handleRoleClick}
                      onSkillClick={handleSkillClick}
                      onEmploymentTypeClick={handleEmploymentTypeClick}
                      onCompanySizeClick={handleCompanySizeClick}
                      onCountryClick={handleCountryClick}
                      onCityClick={handleCityClick}
                      onBenefitClick={handleBenefitClick}
                      onFieldClick={handleFieldClick}
                      onRateClick={handleRateClick}
                    />
                  </ContentColumn>
                  <NavigationColumn
                    collapseOnMobile
                    isHidden={jobsStats.total === 0}
                    isMobileView={isMobileView}
                    isFilterOpened={filterOpened}
                    onFilterToggle={toggleFilterOpened}
                    loading={loadingAggregations}
                  >
                    <FilterWrapper
                      t={t}
                      filterOpened={filterOpened}
                      onFilterClose={handleResetDeferred}
                      onFilterSubmit={handleSubmitDeferred}
                    >
                      <h3 className="sr-only">{t('page.home.filterTitle')}</h3>
                      {AGGREGATIONS.map((def) => {
                        if (def.type === 'rate') {
                          if (aggregations.rate && aggregations.rate.max) {
                            // const current = searchItems.find(si => si.type === 'rate');
                            const current = deferredState.searchItems.find(si => si.type === 'rate');
                            const isNot = current ? current.not : false;
                            return (
                              <RateRangeBox
                                inverse={isNot}
                                currency={currency}
                                key="rate"
                                // current={searchItems.find(si => si.type === 'rate')}
                                current={deferredState.searchItems.find(si => si.type === 'rate')}
                                type="rate"
                                max={getSliderMaxByCurrency(aggregations.rate.max - getSliderStepByCurrency(currency), currency)}
                                step={getSliderStepByCurrency(currency)}
                                title={t(isNot ? 'userportal:page.home.minRateInverse' : 'userportal:page.home.minRate')}
                                onSelect={val => handleSelectRate(val, isNot, isMobileView)}
                                onReset={onRemoveSuggestionByType('rate')}
                              />
                            );
                          }

                          return null;
                        }

                        if (def.type === 'homeoffice') {
                          if (aggregations.homeoffice && aggregations.homeoffice.max > 0) {
                            // const current = searchItems.find(si => si.type === 'homeoffice');
                            const current = deferredState.searchItems.find(si => si.type === 'homeoffice');
                            return (
                              <RangeBox
                                key="homeoffice"
                                type="homeoffice"
                                current={current ? Math.abs(~~current.slug) : -1}
                                formatValueLabel={val => formatHomefficeValueLabel(val, current ? current.not : false)}
                                min={-1}
                                max={aggregations.homeoffice.max}
                                step={1}
                                title={t('common:searchItem.homeoffice')}
                                onSelect={val => handleSelectHomeoffice(val, current ? current.not : false, isMobileView)}
                                onReset={onRemoveSuggestionByType('homeoffice')}
                              />
                            );
                          }

                          return null;
                        }

                        if (!(aggregations[def.key] && aggregations[def.key].buckets && (aggregations[def.key].buckets.length > 0 || !_.isEmpty(aggregations[def.key].buckets)))) {
                          return null;
                        }

                        return (
                          <FacetBox
                            withSeoLinks
                            key={def.key}
                            type={def.type}
                            title={t(`common:searchItem.${def.titleKey}`)}
                            total={jobsStats.total}
                            multiselect={def.multiselect}
                            items={aggregations[def.key].buckets}
                            selected={getSearchItemsByType(def.type)}
                            searchItems={searchItems}
                            onSelect={handleDeferredFacet(def.fn)}
                            maxPreviewItems={def.maxPreviewItems}
                          />
                        );
                      }).filter(Boolean)}
                    </FilterWrapper>
                  </NavigationColumn>
                </div>
              )}
            />
          </div>
        )}
      </Page>
    </>
  );
};

export default HomePage;
